<template>
  <div class="cover-media-picker">
    <button
      type="button"
      class="btn btn-outline-primary rounded w-100 text-start d-flex justify-content-between align-items-center"
      @click="openModal"
    >
      <span><i class="fas fa-image mr-2"></i> Choose Cover Media</span>
      <i class="fas fa-paperclip"></i>
    </button>

    <!-- Selected summary -->
    <div class="mt-3" v-if="visibleSlots.length">
      <div class="media-slots-grid">
        <div v-for="slot in visibleSlots" :key="`media-slot-${slot.index}`" class="position-relative m-1">
          <div class="slot-tile-modern" @click="handleSlotClick(slot)">
            <button
              type="button"
              class="slot-close-icon"
              @click.stop="handleSlotAction(slot)"
              :aria-label="'Remove media'"
            >
              ×
            </button>
            <template v-if="slot.kind === 'image'">
              <img
                v-no-right-click
                :src="slot.data.url || slot.data.preview"
                class="slot-media-image"
              />
            </template>
            <template v-else-if="slot.kind === 'video'">
              <div class="video-slot-fill">
                <video
                  v-if="slot.data?.kind !== 'youtube' && (slot.data?.preview || slot.data?.url)"
                  :src="slot.data.preview || slot.data.url"
                  muted
                  playsinline
                  class="video-slot-preview-modern"
                ></video>
                <img
                  v-if="slot.data?.thumbPreview || slot.data?.thumbUrl || slot.data?.thumbnail"
                  v-no-right-click
                  :src="slot.data.thumbPreview || slot.data.thumbUrl || slot.data.thumbnail"
                  class="video-thumb-preview-modern"
                  alt="Video thumbnail"
                />
                <div class="slot-play-indicator"><i class="fas fa-play"></i></div>
              </div>
            </template>
            <template v-else>
              <div class="slot-empty-hint"></div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-card cover-media-modal" role="dialog" aria-modal="true" tabindex="-1">
        <div class="modal-header">
          <h5 class="modal-title">Choose Cover Media</h5>
          <button class="close" @click="closeModal" aria-label="Close">&times;</button>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-3">Pick a photo, upload a short video, or add a video link.</p>

          <div class="option-grid">
            <button type="button" class="option-tile" :class="{ active: activeOption === 'posters' }" @click="setOption('posters')">
              <i class="fas fa-images"></i>
              <div class="option-title">Posters / GIF</div>
              <div class="option-sub">Pick a themed poster cover</div>
            </button>
            <button type="button" class="option-tile" :class="{ active: activeOption === 'photo' }" @click="setOption('photo')">
              <i class="fas fa-camera"></i>
              <div class="option-title">Upload Photo</div>
              <div class="option-sub">Choose from your phone</div>
            </button>
            <button type="button" class="option-tile" :class="{ active: activeOption === 'video' }" @click="setOption('video')">
              <i class="fas fa-video"></i>
              <div class="option-title">Upload Video</div>
              <div class="option-sub">MP4 up to 100MB</div>
            </button>
            <button type="button" class="option-tile" :class="{ active: activeOption === 'youtube' }" @click="setOption('youtube')">
              <i class="fab fa-youtube"></i>
              <div class="option-title">Video Link</div>
              <div class="option-sub">Paste a link to use</div>
            </button>
          </div>

          <!-- Posters / GIFs -->
          <div v-if="activeOption === 'posters'" class="mt-3">
            <div class="category-tabs overflow-auto d-flex mx-2 mb-3" style="overflow-x:auto !important" v-if="categories.length">
              <button
                type="button"
                v-for="category in categories"
                :key="category.id"
                class="btn btn-outline-dark btn-sm p-1 m-1 px-3"
                style="font-size:12px;white-space:nowrap"
                :class="{ active: selectedCategory && selectedCategory.id === category.id }"
                @click="changeCategory(category)"
              >
                {{ category.name }}
              </button>
            </div>

            <div class="tabset mb-3" role="tablist">
              <button type="button" class="tab-btn" :class="{ active: currentTab === 'Gifs' }" @click="changeTab('Gifs')">GIFs</button>
              <button type="button" class="tab-btn" :class="{ active: currentTab === 'Posters' }" @click="changeTab('Posters')">Posters</button>
            </div>

            <div v-if="currentTab === 'Gifs'" class="row mb-3">
              <div class="col-12 mb-2">
                <input
                  type="text"
                  class="form-control"
                  v-model.trim="search"
                  placeholder="Search GIFs"
                  @keyup.enter="fetchMedia"
                />
              </div>
              <div class="col-md-3 col-6 mb-2 text-center" v-for="img in filteredMedia" :key="img.id">
                <button
                  type="button"
                  class="media-pick-tile"
                  :class="{ 'is-selected': isSelected(img) }"
                  @click="toggleSelect(img)"
                  :aria-label="`Select gif ${img.id}`"
                >
                  <img
                    :src="img.url"
                    class="img-thumbnail media-pick-image"
                    :class="{ 'border-primary': isSelected(img) }"
                    :alt="`gif-${img.id}`"
                  />
                  <span v-if="isSelected(img)" class="media-pick-check">
                    <i class="fas fa-check"></i>
                  </span>
                </button>
              </div>
              <div v-if="filteredMedia.length === 0" class="col-12 text-center py-3">
                <p class="text-muted">No GIFs found</p>
              </div>
            </div>

            <div v-if="currentTab === 'Posters'" class="row mb-3">
              <div class="col-md-3 col-6 mb-2 text-center" v-for="img in filteredMedia" :key="img.id">
                <button
                  type="button"
                  class="media-pick-tile"
                  :class="{ 'is-selected': isSelected(img) }"
                  @click="toggleSelect(img)"
                  :aria-label="`Select poster ${img.id}`"
                >
                  <img
                    :src="img.image"
                    class="img-thumbnail media-pick-image"
                    :class="{ 'border-primary': isSelected(img) }"
                    :alt="`poster-${img.id}`"
                  />
                  <span v-if="isSelected(img)" class="media-pick-check">
                    <i class="fas fa-check"></i>
                  </span>
                </button>
              </div>
              <div v-if="filteredMedia.length === 0" class="col-12 text-center py-3">
                <p class="text-muted">No posters found</p>
              </div>
            </div>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-primary chip-btn" :disabled="addImageDisabled" @click="commitImages">
                {{ replaceIndex !== null ? 'Replace Selected Slot' : 'Add Selected Images' }}
              </button>
              <button v-if="replaceIndex !== null" type="button" class="btn btn-outline-secondary chip-btn" @click="cancelReplace">Cancel Replace</button>
            </div>
          </div>

          <!-- Upload Photo -->
          <div v-if="activeOption === 'photo'" class="mt-3">
            <label for="photoUpload" class="upload-tile d-flex align-items-center justify-content-center">
              <i class="fas fa-cloud-upload-alt mr-2"></i>
              <span>Upload from device</span>
            </label>
            <input
              id="photoUpload"
              type="file"
              ref="photoInput"
              multiple
              @change="handleLocalUpload"
              accept="image/*"
              class="sr-only"
            />
            <div class="media-grid mt-2 d-flex flex-wrap">
              <div v-for="(img, index) in selectedLocal" :key="index" class="position-relative m-1">
                <img v-no-right-click :src="img.preview" class="img-thumbnail" style="width:100px;height:100px;object-fit:cover" />
                <button type="button" class="btn btn-danger btn-sm position-absolute del-btn" @click="removeLocalImage(index)">×</button>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2 mt-2">
              <button type="button" class="btn btn-primary chip-btn" :disabled="addImageDisabled" @click="commitImages">
                {{ replaceIndex !== null ? 'Replace Selected Slot' : 'Add Selected Images' }}
              </button>
              <button v-if="replaceIndex !== null" type="button" class="btn btn-outline-secondary chip-btn" @click="cancelReplace">Cancel Replace</button>
            </div>
          </div>

          <!-- Upload Video -->
          <div v-if="activeOption === 'video'" class="mt-3">
            <label for="videoUpload" class="upload-tile d-flex align-items-center justify-content-center">
              <i class="fas fa-video mr-2"></i>
              <span>Select a video file</span>
            </label>
            <input
              id="videoUpload"
              type="file"
              ref="videoInput"
              @change="handleVideoUpload"
              accept="video/*"
              class="sr-only"
            />
            <label for="videoThumbUpload" class="upload-tile d-flex align-items-center justify-content-center mt-3">
              <i class="fas fa-image mr-2"></i>
              <span>Upload attachment image (thumbnail)</span>
            </label>
            <input
              id="videoThumbUpload"
              type="file"
              ref="videoThumbInput"
              @change="handleVideoThumbUpload"
              accept="image/*"
              class="sr-only"
            />
            <div v-if="videoStaged" class="media-preview-tile mt-3">
              <video v-if="videoStaged.preview" :src="videoStaged.preview" controls class="video-preview"></video>
              <div class="media-meta">
                <div class="fw-semibold">{{ videoStaged.name || 'Selected video' }}</div>
                <div class="small text-muted" v-if="videoStaged.size">{{ formatBytes(videoStaged.size) }}</div>
                <div class="mt-2" v-if="videoStaged.thumbPreview">
                  <img :src="videoStaged.thumbPreview" class="video-thumb-stage" alt="Video thumbnail preview" />
                </div>
              </div>
              <button type="button" class="btn btn-outline-danger chip-btn" @click="clearStagedVideo">Remove</button>
            </div>
            <div class="d-flex align-items-center gap-2 mt-3">
              <button type="button" class="btn btn-primary chip-btn" :disabled="!canCommitVideo" @click="commitVideo">Use This Video</button>
              <button type="button" class="btn btn-outline-secondary chip-btn" @click="clearStagedVideo" :disabled="!videoStaged">Clear</button>
            </div>
          </div>

          <!-- Video Link -->
          <div v-if="activeOption === 'youtube'" class="mt-3">
            <label class="form-label">Video Link</label>
            <input
              type="text"
              class="form-control"
              placeholder="https://www.youtube.com/watch?v=..."
              v-model.trim="youtubeInput"
              @input="updateYoutubePreview"
            />
            <label for="youtubeThumbUpload" class="upload-tile d-flex align-items-center justify-content-center mt-3">
              <i class="fas fa-image mr-2"></i>
              <span>Upload custom video thumbnail</span>
            </label>
            <input
              id="youtubeThumbUpload"
              type="file"
              ref="youtubeThumbInput"
              @change="handleYoutubeThumbUpload"
              accept="image/*"
              class="sr-only"
            />
            <div v-if="youtubePreview" class="media-preview-tile mt-3">
              <img v-if="youtubePreview.thumbnail" :src="youtubePreview.thumbnail" alt="Video thumbnail" class="yt-thumb" />
              <div v-else class="small text-muted">Thumbnail not available for this link.</div>
              <div class="media-meta">
                <div class="fw-semibold">Video Link</div>
                <div class="small text-muted">{{ youtubePreview.url }}</div>
                <button
                  v-if="youtubePreview.thumbnail"
                  type="button"
                  class="btn btn-link btn-sm p-0 mt-2"
                  @click="removeLinkThumbnail"
                >
                  Remove thumbnail
                </button>
              </div>
              <button type="button" class="btn btn-outline-danger chip-btn" @click="clearYoutubePreview">Remove</button>
            </div>
            <div class="d-flex align-items-center gap-2 mt-3">
              <button type="button" class="btn btn-primary chip-btn" :disabled="!youtubePreview" @click="commitYoutube">Use This Link</button>
              <button type="button" class="btn btn-outline-secondary chip-btn" @click="clearYoutubePreview" :disabled="!youtubePreview">Clear</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline-secondary chip-btn" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, toRefs } from "vue";
import Swal from "sweetalert2";
import api from "@/services/api";

const toast = (msg, icon = "success") =>
  Swal.fire({ toast: true, position: "top-end", icon, title: msg, showConfirmButton: false, timer: 2000 });

const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const ALLOWED_VIDEO_EXT = ["mp4", "mov", "webm", "m4v", "mkv", "avi"];
const DIRECT_VIDEO_EXT_RE = /\.(mp4|mov|webm|m4v|mkv|avi)(\?.*)?$/i;
const LINK_THUMB_TIMEOUT = 8000;

export default {
  name: "CoverMediaPicker",
  props: {
    modelValue: {
      type: Object,
      default: () => ({ type: "images", images: [], video: null, youtube: null })
    },
    mode: {
      type: String,
      default: "add"
    },
    maxImages: {
      type: Number,
      default: 3
    }
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { mode, maxImages } = toRefs(props);
    const showModal = ref(false);
    const activeOption = ref("posters");

    const committedImages = ref([]);
    const selected = ref([]);
    const selectedLocal = ref([]);

    const replaceIndex = ref(null);

    const search = ref("");
    const currentTab = ref("Posters");
    const selectedCategory = ref(null);
    const categories = ref([]);
    const allMedia = ref([]);

    const photoInput = ref(null);
    const videoInput = ref(null);
    const videoThumbInput = ref(null);

    const videoStaged = ref(null);
    const youtubeInput = ref("");
    const youtubePreview = ref(null);
    const youtubeThumbInput = ref(null);
    const targetVideoSlot = ref(null);
    const linkFetchSeq = ref(0);

    const model = computed(() => props.modelValue || { type: "images", images: [], video: null, youtube: null });

    const committedCount = computed(() => {
      return mode.value === "edit"
        ? committedImages.value.filter(Boolean).length
        : committedImages.value.length;
    });

    const hasAnySelection = computed(() => {
      return committedCount.value > 0 || !!model.value.video || !!model.value.youtube;
    });
    const canCommitVideo = computed(() => !!videoStaged.value?.file && !!videoStaged.value?.thumbFile);
    const totalSlots = computed(() => maxImages.value + 1);

    const activeVideoData = computed(() => {
      if (model.value.youtube) return { ...model.value.youtube, kind: "youtube" };
      if (model.value.video) return { ...model.value.video, kind: "video" };
      return null;
    });

    const autoVideoSlot = computed(() => {
      const existingRaw = activeVideoData.value?.slotIndex;
      if (existingRaw !== null && existingRaw !== undefined && existingRaw !== "") {
        const existing = Number(existingRaw);
        if (Number.isInteger(existing) && existing >= 0 && existing < totalSlots.value) return existing;
      }
      const imageCount = committedImages.value.filter(Boolean).length;
      return Math.min(imageCount, totalSlots.value - 1);
    });

    const resolveVideoSlot = () => {
      const targetRaw = targetVideoSlot.value;
      if (targetRaw !== null && targetRaw !== undefined && targetRaw !== "") {
        const target = Number(targetRaw);
        if (Number.isInteger(target) && target >= 0 && target < totalSlots.value) return target;
      }
      return autoVideoSlot.value;
    };

    const displaySlots = computed(() => {
      const slots = Array.from({ length: totalSlots.value }, (_, index) => ({
        index,
        kind: "empty",
        data: null,
        imageOrderIndex: null,
      }));
      const video = activeVideoData.value;
      if (video) {
        const raw = video.slotIndex;
        const parsed = (raw !== null && raw !== undefined && raw !== "") ? Number(raw) : NaN;
        const slotIdx = Number.isInteger(parsed) && parsed >= 0 && parsed < totalSlots.value ? parsed : autoVideoSlot.value;
        slots[slotIdx] = { index: slotIdx, kind: "video", data: video, imageOrderIndex: null };
      }
      const images = committedImages.value.filter(Boolean);
      let order = 0;
      for (let i = 0; i < slots.length && order < images.length; i++) {
        if (slots[i].kind !== "empty") continue;
        slots[i] = { index: i, kind: "image", data: images[order], imageOrderIndex: order };
        order++;
      }
      return slots;
    });
    const visibleSlots = computed(() => displaySlots.value.filter((slot) => slot.kind !== "empty"));

    const filteredMedia = computed(() => (currentTab.value === "Upload" ? [] : allMedia.value));

    const addImageDisabled = computed(() => {
      const staged = selected.value.length + selectedLocal.value.length;
      if (replaceIndex.value !== null) return staged === 0;
      return staged === 0 || committedCount.value >= maxImages.value;
    });

    const normalizeForMode = (images) => {
      const list = Array.isArray(images) ? images.slice(0, maxImages.value) : [];
      if (mode.value !== "edit") return [...list];
      const slots = Array(maxImages.value).fill(null);
      list.forEach((img, idx) => { slots[idx] = img; });
      return slots;
    };

    watch(
      () => props.modelValue,
      (val) => {
        committedImages.value = normalizeForMode(val?.images);
      },
      { immediate: true, deep: true }
    );

    const openModal = async ({ preserveTarget = false } = {}) => {
      if (!preserveTarget) targetVideoSlot.value = null;
      showModal.value = true;
      if (!categories.value.length) await fetchCategories();
      if (!selectedCategory.value && categories.value.length) selectedCategory.value = categories.value[0];
      if (currentTab.value !== "Upload") await fetchMedia();
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const setOption = (opt) => {
      activeOption.value = opt;
      if (opt === "posters") {
        currentTab.value = "Posters";
        if (!categories.value.length) fetchCategories();
        fetchMedia();
      }
    };

    const canonicalizeSrc = (src) => {
      if (!src) return "";
      const s = String(src).trim();
      if (s.startsWith("data:")) return s;
      try {
        const u = new URL(s);
        return (u.origin + u.pathname).toLowerCase();
      } catch {
        return s.toLowerCase().split("?")[0].split("#")[0];
      }
    };

    const isSelected = (img) => {
      if (currentTab.value === "Upload") return false;
      const url = img?.url || img?.image || "";
      const canon = canonicalizeSrc(url);
      if (committedImages.value.filter(Boolean).some((c) => canonicalizeSrc(c.url || c.preview) === canon)) return true;
      return selected.value.some((s) => s.id === img.id);
    };

    const toggleSelect = (img) => {
      if (currentTab.value === "Upload") return;
      if (isSelected(img)) {
        selected.value = selected.value.filter((s) => s.id !== img.id);
        return;
      }

      if (replaceIndex.value === null && committedCount.value >= maxImages.value) {
        toast(`Maximum ${maxImages.value} images allowed`, "warning");
        return;
      }

      if (replaceIndex.value !== null) {
        selected.value = [{ id: img.id, url: img.url || img.image, image: img.image }];
        selectedLocal.value = [];
        return;
      }

      selected.value.push(img);
    };

    const handleLocalUpload = (e) => {
      const files = Array.from(e.target.files || []);
      const maxAdds = replaceIndex.value !== null
        ? 1
        : maxImages.value - committedCount.value - selected.value.length - selectedLocal.value.length;

      if (maxAdds <= 0) {
        toast(`Maximum ${maxImages.value} images allowed`, "warning");
        if (photoInput.value) photoInput.value.value = "";
        return;
      }

      let added = 0;
      const canonSet = new Set(committedImages.value.filter(Boolean).map((c) => canonicalizeSrc(c.url || c.preview)));

      files.forEach((file) => {
        if (added >= maxAdds) return;
        if (!file.type.startsWith("image/")) return;
        const preview = URL.createObjectURL(file);
        const canon = canonicalizeSrc(preview);
        if (canonSet.has(canon)) return;

        if (replaceIndex.value !== null) {
          selectedLocal.value = [{ file, preview }];
        } else {
          selectedLocal.value.push({ file, preview });
        }
        added++;
      });

      if (photoInput.value) photoInput.value.value = "";
    };

    const removeLocalImage = (idx) => {
      selectedLocal.value.splice(idx, 1);
    };

    const emitModel = (patch) => {
      emit("update:modelValue", { ...props.modelValue, ...patch });
    };

    const revokeIfBlob = (url) => {
      if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url);
    };

    const currentType = (video, youtube) => {
      if (youtube) return "youtube";
      if (video) return "video";
      return "images";
    };

    const commitImages = () => {
      const canonSet = new Set(committedImages.value.filter(Boolean).map((c) => canonicalizeSrc(c.url || c.preview)));

      if (replaceIndex.value !== null) {
        const remote = selected.value[0];
        const local = selectedLocal.value[0];
        if (!remote && !local) {
          toast("Select one image to replace", "warning");
          return;
        }
        const chosen = remote
          ? { url: remote.url || remote.image, isRemote: true, id: remote.id }
          : { preview: local.preview, isLocal: true, file: local.file };

        const canon = canonicalizeSrc(chosen.url || chosen.preview);
        if (canonSet.has(canon)) {
          toast("This image is already added", "warning");
          return;
        }

        const current = committedImages.value.filter(Boolean);
        current.splice(replaceIndex.value, 1, chosen);
        committedImages.value = mode.value === "edit" ? normalizeForMode(current) : current;
        replaceIndex.value = null;
        selected.value = [];
        selectedLocal.value = [];
        clearStagedVideo();
        clearYoutubePreview();
        emitModel({
          type: currentType(model.value.video || null, model.value.youtube || null),
          images: current,
          video: model.value.video || null,
          youtube: model.value.youtube || null
        });
        targetVideoSlot.value = null;
        toast("Image replaced", "success");
        return;
      }

      const remaining = maxImages.value - committedCount.value;
      if (remaining <= 0) {
        toast(`Maximum ${maxImages.value} images allowed`, "warning");
        return;
      }

      const remoteToAdd = selected.value.filter((img) => {
        const u = img.url || img.image;
        const canon = canonicalizeSrc(u);
        return u && canon && !canonSet.has(canon);
      }).slice(0, remaining);

      const localRemaining = remaining - remoteToAdd.length;
      const localToAdd = selectedLocal.value.filter((li) => {
        const canon = canonicalizeSrc(li.preview);
        return li.preview && canon && !canonSet.has(canon);
      }).slice(0, localRemaining);

      const next = mode.value === "edit" ? normalizeForMode(committedImages.value) : [...committedImages.value];
      const addItem = (item) => {
        if (mode.value !== "edit") {
          next.push(item);
          return;
        }
        const idx = next.findIndex((v) => v == null);
        if (idx >= 0) next.splice(idx, 1, item);
      };
      remoteToAdd.forEach((img) => addItem({ url: img.url || img.image, isRemote: true, id: img.id }));
      localToAdd.forEach((li) => addItem({ preview: li.preview, isLocal: true, file: li.file }));

      committedImages.value = next;
      selected.value = [];
      selectedLocal.value = [];
      clearStagedVideo();
      clearYoutubePreview();
      emitModel({
        type: currentType(model.value.video || null, model.value.youtube || null),
        images: committedImages.value.filter(Boolean),
        video: model.value.video || null,
        youtube: model.value.youtube || null
      });
      targetVideoSlot.value = null;
      toast("Images added", "success");
    };

    const openReplace = (idx) => {
      replaceIndex.value = idx;
      showModal.value = true;
      activeOption.value = "posters";
    };

    const handleSlotClick = (slot) => {
      if (slot.kind === "image") {
        openReplace(slot.imageOrderIndex);
        return;
      }
      if (slot.kind === "video") {
        openVideoReplace(slot.index);
        return;
      }
      targetVideoSlot.value = slot.index;
      replaceIndex.value = null;
      activeOption.value = "posters";
      openModal({ preserveTarget: true });
    };

    const handleSlotAction = (slot) => {
      if (slot.kind === "image") {
        removeCommitted(slot.imageOrderIndex);
        return;
      }
      if (slot.kind === "video") {
        clearVideo();
        return;
      }
      handleSlotClick(slot);
    };

    const cancelReplace = () => {
      replaceIndex.value = null;
      selected.value = [];
      selectedLocal.value = [];
    };

    const removeCommitted = (idx) => {
      const list = committedImages.value.filter(Boolean);
      list.splice(idx, 1);
      committedImages.value = mode.value === "edit" ? normalizeForMode(list) : list;
      emitModel({
        type: currentType(model.value.video || null, model.value.youtube || null),
        images: list,
        video: model.value.video || null,
        youtube: model.value.youtube || null
      });
    };

    const handleVideoUpload = (e) => {
      const file = (e.target.files || [])[0];
      if (!file) return;
      const ext = (file.name || "").split(".").pop().toLowerCase();
      if (!ALLOWED_VIDEO_EXT.includes(ext)) {
        toast(`Only ${ALLOWED_VIDEO_EXT.join(", ")} files allowed`, "error");
        if (videoInput.value) videoInput.value.value = "";
        return;
      }
      if (file.size > MAX_VIDEO_BYTES) {
        toast("Video must be 100MB or smaller", "error");
        if (videoInput.value) videoInput.value.value = "";
        return;
      }
      const preview = URL.createObjectURL(file);
      videoStaged.value = {
        file,
        preview,
        name: file.name,
        size: file.size,
        thumbFile: videoStaged.value?.thumbFile || null,
        thumbPreview: videoStaged.value?.thumbPreview || "",
      };
      if (videoInput.value) videoInput.value.value = "";
    };

    const handleVideoThumbUpload = (e) => {
      const file = (e.target.files || [])[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast("Thumbnail must be an image", "error");
        if (videoThumbInput.value) videoThumbInput.value.value = "";
        return;
      }
      const thumbPreview = URL.createObjectURL(file);
      videoStaged.value = {
        ...(videoStaged.value || {}),
        thumbFile: file,
        thumbPreview,
      };
      if (videoThumbInput.value) videoThumbInput.value.value = "";
    };

    const clearStagedVideo = () => {
      revokeIfBlob(videoStaged.value?.preview);
      revokeIfBlob(videoStaged.value?.thumbPreview);
      videoStaged.value = null;
      if (videoInput.value) videoInput.value.value = "";
      if (videoThumbInput.value) videoThumbInput.value.value = "";
    };

    const commitVideo = () => {
      if (!videoStaged.value?.file) {
        toast("Select a video file", "warning");
        return;
      }
      if (!videoStaged.value?.thumbFile) {
        toast("Upload a thumbnail image for the video", "warning");
        return;
      }
      selected.value = [];
      selectedLocal.value = [];
      replaceIndex.value = null;
      const slotIndex = resolveVideoSlot();
      const oldVideo = model.value.video;
      const oldYoutube = model.value.youtube;
      const committedVideoPreview = URL.createObjectURL(videoStaged.value.file);
      const committedThumbPreview = URL.createObjectURL(videoStaged.value.thumbFile);
      emitModel({
        type: "video",
        video: {
          file: videoStaged.value.file,
          preview: committedVideoPreview,
          name: videoStaged.value.name,
          size: videoStaged.value.size,
          thumbFile: videoStaged.value.thumbFile,
          thumbPreview: committedThumbPreview,
          isLocal: true,
          slotIndex,
        },
        images: committedImages.value.filter(Boolean),
        youtube: null
      });
      revokeIfBlob(oldVideo?.preview);
      revokeIfBlob(oldVideo?.thumbPreview);
      revokeIfBlob(oldYoutube?.thumbPreview);
      clearStagedVideo();
      targetVideoSlot.value = null;
      toast("Video selected", "success");
    };

    const clearVideo = () => {
      revokeIfBlob(model.value.video?.preview);
      revokeIfBlob(model.value.video?.thumbPreview);
      revokeIfBlob(model.value.youtube?.thumbPreview);
      emitModel({ type: "images", video: null, youtube: null, images: committedImages.value.filter(Boolean) });
      clearStagedVideo();
      targetVideoSlot.value = null;
    };

    const openVideoReplace = (slotIndex = null) => {
      targetVideoSlot.value = slotIndex;
      activeOption.value = "video";
      openModal({ preserveTarget: true });
    };

    const parseYouTubeId = (url) => {
      if (!url) return null;
      const s = String(url).trim();
      const match = s.match(/(?:v=|\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
      return match ? match[1] : null;
    };

    const normalizeLink = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return "";
      const withProto = /^https?:\/\//i.test(s) ? s : `https://${s}`;
      try {
        return new URL(withProto).toString();
      } catch {
        return "";
      }
    };

    const withTimeout = async (promise, ms = LINK_THUMB_TIMEOUT) => {
      let timer = null;
      try {
        return await Promise.race([
          promise,
          new Promise((resolve) => {
            timer = setTimeout(() => resolve(null), ms);
          }),
        ]);
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    const dataUrlToFile = (dataUrl, name = "video_thumb.jpg") => {
      try {
        const parts = String(dataUrl || "").split(",");
        if (parts.length < 2) return null;
        const meta = parts[0] || "";
        const b64 = parts[1] || "";
        const mimeMatch = meta.match(/data:([^;]+);base64/i);
        const mime = mimeMatch?.[1] || "image/jpeg";
        const bin = atob(b64);
        const len = bin.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
        return new File([bytes], name, { type: mime });
      } catch {
        return null;
      }
    };

    const fetchImageAsFile = async (url, name = "video_thumb.jpg") => {
      try {
        const res = await withTimeout(fetch(url), 6000);
        if (!res || !res.ok) return null;
        const blob = await res.blob();
        if (!blob || !blob.size) return null;
        const ext = (blob.type || "image/jpeg").includes("png") ? "png" : "jpg";
        return new File([blob], name.replace(/\.\w+$/, `.${ext}`), { type: blob.type || "image/jpeg" });
      } catch {
        return null;
      }
    };

    const ensureThumbFileFromSource = async (src) => {
      const s = String(src || "").trim();
      if (!s) return null;
      if (s.startsWith("data:image/")) {
        return dataUrlToFile(s, `video_thumb_${Date.now()}.jpg`);
      }
      if (/^https?:\/\//i.test(s)) {
        return await fetchImageAsFile(s, `video_thumb_${Date.now()}.jpg`);
      }
      return null;
    };

    const getNoembedThumb = async (url) => {
      try {
        const endpoint = `https://noembed.com/embed?url=${encodeURIComponent(url)}`;
        const res = await withTimeout(fetch(endpoint), 5000);
        if (!res || !res.ok) return "";
        const data = await res.json();
        return String(
          data?.thumbnail_url ||
          data?.thumbnail_url_with_play_button ||
          ""
        ).trim();
      } catch {
        return "";
      }
    };

    const getDirectVideoThumb = (url) =>
      new Promise((resolve) => {
        let done = false;
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = "anonymous";

        const finish = (thumb = "") => {
          if (done) return;
          done = true;
          cleanup();
          resolve(thumb || "");
        };

        const cleanup = () => {
          video.removeEventListener("loadeddata", onLoadedData);
          video.removeEventListener("seeked", onSeeked);
          video.removeEventListener("error", onError);
          video.src = "";
        };

        const onError = () => finish("");
        const onLoadedData = () => {
          try {
            video.currentTime = Math.min(1, Number(video.duration) || 1);
          } catch {
            onSeeked();
          }
        };
        const onSeeked = () => {
          try {
            const w = video.videoWidth || 640;
            const h = video.videoHeight || 360;
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) return finish("");
            ctx.drawImage(video, 0, 0, w, h);
            finish(canvas.toDataURL("image/jpeg", 0.82));
          } catch {
            finish("");
          }
        };

        video.addEventListener("loadeddata", onLoadedData);
        video.addEventListener("seeked", onSeeked);
        video.addEventListener("error", onError);
        video.src = url;

        setTimeout(() => finish(""), 6000);
      });

    const fetchLinkThumbnail = async (url) => {
      if (!url) return "";
      const ytId = parseYouTubeId(url);
      if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;

      const lower = url.toLowerCase();
      const looksSocial =
        lower.includes("facebook.com") ||
        lower.includes("fb.watch") ||
        lower.includes("instagram.com") ||
        lower.includes("linkedin.com");

      if (looksSocial) {
        const socialThumb = await getNoembedThumb(url);
        if (socialThumb) return socialThumb;
      }

      if (DIRECT_VIDEO_EXT_RE.test(url)) {
        const directThumb = await getDirectVideoThumb(url);
        if (directThumb) return directThumb;
      }

      return await getNoembedThumb(url);
    };

    const updateYoutubePreview = async () => {
      const normalizedUrl = normalizeLink(youtubeInput.value);
      if (!normalizedUrl) {
        youtubePreview.value = null;
        return;
      }

      const requestSeq = ++linkFetchSeq.value;
      const previousUrl = String(youtubePreview.value?.url || "");
      const keepCustomThumb =
        previousUrl === normalizedUrl &&
        youtubePreview.value?.thumbFile instanceof File;
      const preservedThumb = keepCustomThumb
        ? String(youtubePreview.value?.thumbPreview || "")
        : "";
      const preservedFile = keepCustomThumb ? youtubePreview.value?.thumbFile : null;

      youtubePreview.value = {
        url: normalizedUrl,
        thumbnail: preservedThumb || "",
        thumbUrl: "",
        thumbPreview: preservedThumb,
        thumbFile: preservedFile,
      };

      const fetchedThumb = keepCustomThumb
        ? preservedThumb
        : String((await fetchLinkThumbnail(normalizedUrl)) || "").trim();

      if (requestSeq !== linkFetchSeq.value) return;

      youtubePreview.value = {
        url: normalizedUrl,
        thumbnail: fetchedThumb || preservedThumb || "",
        thumbUrl: keepCustomThumb ? "" : fetchedThumb,
        thumbPreview: preservedThumb,
        thumbFile: preservedFile,
      };
    };

    const handleYoutubeThumbUpload = (e) => {
      const file = (e.target.files || [])[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast("Thumbnail must be an image", "error");
        if (youtubeThumbInput.value) youtubeThumbInput.value.value = "";
        return;
      }
      if (youtubePreview.value?.thumbPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(youtubePreview.value.thumbPreview);
      }
      const thumbPreview = URL.createObjectURL(file);
      youtubePreview.value = {
        ...(youtubePreview.value || { url: youtubeInput.value, thumbnail: "" }),
        thumbnail: thumbPreview,
        thumbUrl: "",
        thumbPreview,
        thumbFile: file,
      };
      if (youtubeThumbInput.value) youtubeThumbInput.value.value = "";
    };

    const clearYoutubePreview = () => {
      revokeIfBlob(youtubePreview.value?.thumbPreview);
      youtubeInput.value = "";
      youtubePreview.value = null;
      if (youtubeThumbInput.value) youtubeThumbInput.value.value = "";
    };

    const removeLinkThumbnail = () => {
      if (!youtubePreview.value) return;
      revokeIfBlob(youtubePreview.value.thumbPreview);
      youtubePreview.value = {
        ...youtubePreview.value,
        thumbnail: "",
        thumbUrl: "",
        thumbPreview: "",
        thumbFile: null,
      };
      if (youtubeThumbInput.value) youtubeThumbInput.value.value = "";
    };

    const commitYoutube = async () => {
      if (!youtubePreview.value) return;
      selected.value = [];
      selectedLocal.value = [];
      replaceIndex.value = null;
      const slotIndex = resolveVideoSlot();
      const oldVideo = model.value.video;
      const oldYoutube = model.value.youtube;
      const stagedThumbPreview = String(youtubePreview.value?.thumbPreview || "");
      let committedThumbFile = youtubePreview.value?.thumbFile instanceof File
        ? youtubePreview.value.thumbFile
        : null;
      let committedThumbPreview = "";
      let committedThumbSource = String(youtubePreview.value?.thumbnail || "");

      if (!committedThumbFile && committedThumbSource) {
        committedThumbFile = await ensureThumbFileFromSource(youtubePreview.value.thumbnail);
      }
      if (committedThumbFile) {
        committedThumbPreview = URL.createObjectURL(committedThumbFile);
        committedThumbSource = committedThumbPreview;
      } else {
        committedThumbPreview = stagedThumbPreview;
      }
      emitModel({
        type: "youtube",
        youtube: {
          ...youtubePreview.value,
          thumbnail: committedThumbSource,
          thumbPreview: committedThumbPreview,
          thumbFile: committedThumbFile,
          slotIndex
        },
        images: committedImages.value.filter(Boolean),
        video: null
      });
      revokeIfBlob(oldVideo?.preview);
      revokeIfBlob(oldVideo?.thumbPreview);
      revokeIfBlob(oldYoutube?.thumbPreview);
      clearStagedVideo();
      clearYoutubePreview();
      targetVideoSlot.value = null;
      toast("Video link selected", "success");
    };

    const clearYoutube = () => {
      revokeIfBlob(model.value.youtube?.thumbPreview);
      emitModel({ type: "images", youtube: null, images: committedImages.value.filter(Boolean), video: model.value.video || null });
      targetVideoSlot.value = null;
    };

    const formatBytes = (bytes) => {
      if (!bytes && bytes !== 0) return "";
      const sizes = ["Bytes", "KB", "MB", "GB"];
      if (bytes === 0) return "0 Bytes";
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
    };

    const GIPHY_KEY = process.env.VUE_APP_GIPHY_API_KEY || "aKIJbMMqwwndypRfzeWy9HrVeKNXStCE";

    const fetchCategories = async () => {
      try {
        if (!api.getPosterCategories) return;
        const res = await api.getPosterCategories();
        const list = res?.data?.list || res?.data?.data || res?.data || [];
        categories.value = Array.isArray(list) ? list : [];
        if (!selectedCategory.value && categories.value.length) selectedCategory.value = categories.value[0];
      } catch (e) {
        categories.value = [];
      }
    };

    const fetchMedia = async () => {
      try {
        if (currentTab.value === "Gifs") {
          const q = (search.value || selectedCategory.value?.name || "fun").trim();
          let gData = null;
          if (api.getGiphy) {
            try {
              const params = { api_key: GIPHY_KEY, q, limit: 24, rating: "pg" };
              const res = await api.getGiphy(params);
              gData = res?.data?.data || res?.data || null;
            } catch (e) {
              // fall through
            }
          }
          if (!gData) {
            const url = `https://api.giphy.com/v1/gifs/search?api_key=${encodeURIComponent(GIPHY_KEY)}&q=${encodeURIComponent(q)}&limit=24&rating=pg&lang=en`;
            const res = await fetch(url);
            const json = await res.json();
            gData = json?.data || [];
          }
          allMedia.value = (gData || [])
            .map((g) => ({
              id: g.id,
              url: g.images?.fixed_width?.url || g.images?.downsized_medium?.url || g.images?.original?.url || "",
            }))
            .filter((x) => x.url);
        } else if (currentTab.value === "Posters") {
          if (!selectedCategory.value && categories.value.length) selectedCategory.value = categories.value[0];
          const fd = new FormData();
          fd.append("category", selectedCategory.value?.id || "");
          const res = await api.getPosters(fd);
          const list = res?.data?.list || [];
          allMedia.value = list.map((p) => ({ id: p.id, image: p.image })).filter((p) => p.image);
        } else {
          allMedia.value = [];
        }
      } catch (e) {
        allMedia.value = [];
      }
    };

    const changeTab = async (tab) => {
      currentTab.value = tab;
      selected.value = [];
      if (tab !== "Upload") await fetchMedia();
    };

    const changeCategory = async (cat) => {
      selectedCategory.value = cat;
      if (currentTab.value !== "Upload") await fetchMedia();
    };

    let gifDebounce = null;
    watch(search, () => {
      if (currentTab.value !== "Gifs") return;
      clearTimeout(gifDebounce);
      gifDebounce = setTimeout(() => fetchMedia(), 400);
    });

    return {
      model,
      modeValue: mode,
      maxImagesValue: maxImages,
      showModal,
      activeOption,
      committedImages,
      selectedLocal,
      selected,
      replaceIndex,
      search,
      currentTab,
      selectedCategory,
      categories,
      filteredMedia,
      photoInput,
      videoInput,
      videoThumbInput,
      videoStaged,
      youtubeInput,
      youtubePreview,
      youtubeThumbInput,
      targetVideoSlot,
      displaySlots,
      visibleSlots,
      hasAnySelection,
      canCommitVideo,
      addImageDisabled,
      openModal,
      closeModal,
      setOption,
      isSelected,
      toggleSelect,
      handleLocalUpload,
      removeLocalImage,
      commitImages,
      openReplace,
      handleSlotClick,
      handleSlotAction,
      cancelReplace,
      removeCommitted,
      handleVideoUpload,
      handleVideoThumbUpload,
      clearStagedVideo,
      commitVideo,
      clearVideo,
      openVideoReplace,
      updateYoutubePreview,
      handleYoutubeThumbUpload,
      clearYoutubePreview,
      removeLinkThumbnail,
      commitYoutube,
      clearYoutube,
      changeTab,
      changeCategory,
      fetchMedia,
      formatBytes,
    };
  }
};
</script>

<style scoped>
.cover-media-modal{ max-width:820px; }
.option-grid{ display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:12px; }
.option-tile{
  border:1px solid #f1c6d5; background:#fff7fb; border-radius:12px; padding:12px;
  display:flex; flex-direction:column; align-items:flex-start; gap:6px; text-align:left;
  transition:all .15s ease; cursor:pointer; min-height:92px;
}
.option-tile i{ font-size:20px; color:#e1568a; }
.option-tile.active{ border-color:#e1568a; box-shadow:0 10px 22px rgba(225,86,138,.2); }
.option-title{ font-weight:700; }
.option-sub{ font-size:12px; color:#6b7280; }
.chip-btn{
  border-radius:999px;
  min-height:36px;
  padding:.45rem .95rem;
  font-weight:700;
  font-size:.92rem;
  line-height:1.1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:.35rem;
  white-space:nowrap;
}

.upload-tile{
  border:2px dashed #cbd5e1; background:#f8fafc; border-radius:14px; padding:1rem; cursor:pointer;
  font-weight:700; color:#0f172a; transition:all .15s ease;
}
.upload-tile:hover{ background:#eef2f7; border-color:#94a3b8; }

.tabset{ display:flex; gap:8px; border-bottom:1px solid #e9ecef; margin-bottom:1rem; flex-wrap:wrap; }
.tab-btn{ appearance:none; border:0; background:transparent; padding:.5rem .75rem; border-radius:.25rem .25rem 0 0; font-weight:600; color:#6c757d; cursor:pointer; }
.tab-btn.active{ color:#212529; background:#f8f9fa; border:1px solid #e9ecef; border-bottom-color:transparent; }

.media-pick-tile{
  position:relative;
  width:100%;
  padding:0;
  border:0;
  background:transparent;
  border-radius:10px;
  overflow:hidden;
  cursor:pointer;
}
.media-pick-image{
  width:100%;
  height:100px;
  object-fit:cover;
  transition:transform .12s ease, box-shadow .12s ease;
}
.media-pick-tile.is-selected .media-pick-image{
  border-color:#198754 !important;
  box-shadow:0 0 0 2px rgba(25,135,84,.25);
}
.media-pick-check{
  position:absolute;
  top:8px;
  right:8px;
  width:22px;
  height:22px;
  border-radius:50%;
  background:#198754;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:12px;
  box-shadow:0 4px 10px rgba(25,135,84,.35);
  pointer-events:none;
}

.slot-tile{
  width:130px; height:130px; background:#f8f9fa; border:1px dashed #ced4da; position:relative;
  display:flex; align-items:center; justify-content:center; border-radius:8px; overflow:hidden;
}
.slot-actions{
  position:absolute; bottom:6px; left:6px; right:6px; display:flex; justify-content:space-between;
}
.btn-xs{ padding:2px 6px; font-size:12px; line-height:1.2; }
.media-slots-grid{ display:flex; flex-wrap:wrap; gap:8px; }
.slot-tile-modern{
  width:112px;
  height:150px;
  border-radius:2px;
  background:#d4d4d4;
  position:relative;
  overflow:hidden;
  cursor:pointer;
}
.slot-close-icon{
  position:absolute;
  top:6px;
  right:6px;
  width:16px;
  height:16px;
  border:0;
  border-radius:50%;
  background:#111;
  color:#fff;
  font-size:12px;
  line-height:16px;
  padding:0;
  z-index:3;
}
.slot-media-image{
  width:100%;
  height:100%;
  object-fit:cover;
}
.video-slot-fill{
  width:100%;
  height:100%;
  position:relative;
}
.video-slot-preview-modern{
  width:100%;
  height:100%;
  object-fit:cover;
  background:#000;
  position:absolute;
  inset:0;
}
.video-thumb-preview-modern{
  width:100%;
  height:100%;
  object-fit:cover;
  position:absolute;
  inset:0;
}
.slot-play-indicator{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  pointer-events:none;
}
.slot-play-indicator i{
  width:36px;
  height:36px;
  border-radius:50%;
  background:rgba(255,255,255,.9);
  color:#111;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:14px;
}
.slot-empty-hint{
  width:100%;
  height:100%;
  background:#cfcfcf;
}

.media-preview-tile{
  display:flex; align-items:center; gap:12px; padding:12px; border:1px solid #e5e7eb; border-radius:12px; background:#fff;
}
.video-preview{ width:180px; height:110px; object-fit:cover; border-radius:10px; background:#000; }
.video-thumb-stage{ width:90px; height:60px; object-fit:cover; border-radius:8px; border:1px solid #e5e7eb; }
.yt-thumb{ width:180px; height:110px; object-fit:cover; border-radius:10px; }
.media-meta{ flex:1; min-width:0; }

.del-btn{ top:0; right:0; height:20px!important; width:20px!important; border-radius:50%; line-height:7px; padding:2px; }

.modal-backdrop-custom{
  position:fixed; inset:0; background:rgba(15,23,42,.4); z-index:2000;
  display:flex; align-items:center; justify-content:center; padding:12px;
}
.modal-card{
  width:min(720px, 100%); background:#fff; border-radius:12px; box-shadow:0 24px 60px rgba(2,6,23,.25);
  max-height:85vh; overflow:auto; outline:0;
}
.modal-header, .modal-footer{ padding:12px 16px; border-bottom:1px solid #eee; }
.modal-footer{ border-top:1px solid #eee; border-bottom:0; }
.modal-title{ margin:0; }
.close{ border:0; background:transparent; font-size:24px; line-height:1; }

@media (max-width: 576px){
  .option-grid{ grid-template-columns:1fr; }
  .media-preview-tile{ flex-direction:column; align-items:flex-start; }
  .video-preview, .yt-thumb{ width:100%; height:auto; }
}
</style>
